import { formatDate } from '@angular/common';

/** The low end of the 6–9 months we ask for. The client may carry on without them. */
const MONTHS_ASKED = 6;
/** §A3.7: statements more than 90 days old go stale and the client is asked to refresh. */
const STALE_AFTER_DAYS = 90;
const MS_PER_DAY = 86_400_000;

/** A statement's printed period, as ISO dates. */
export interface StatementPeriod {
  start: string;
  end: string;
}

export interface StatementCoverage {
  /** What is on file and, where we would like more, exactly which months. */
  message: string;
  /** We are asking for more statements on this account. */
  needsMore: boolean;
}

function dayNumber(iso: string): number {
  const [year, month, day] = iso.split('-').map(Number);
  return Date.UTC(year, month - 1, day) / MS_PER_DAY;
}

/** Calendar month as one number, so that consecutive months differ by exactly one. */
function monthIndex(day: number): number {
  const date = new Date(day * MS_PER_DAY);
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
}

function daysInMonth(month: number): number {
  return new Date(Date.UTC(Math.floor(month / 12), (month % 12) + 1, 0)).getUTCDate();
}

function monthLabel(month: number, format: string): string {
  const date = Date.UTC(Math.floor(month / 12), month % 12, 1);
  return formatDate(date, format, 'en-ZA', 'UTC');
}

/** "Jun 2025", "Jan–Apr 2026", "Nov 2025–Feb 2026". */
function rangeLabel(first: number, last: number): string {
  if (first === last) return monthLabel(first, 'MMM yyyy');
  const sameYear = Math.floor(first / 12) === Math.floor(last / 12);
  return `${monthLabel(first, sameYear ? 'MMM' : 'MMM yyyy')}–${monthLabel(last, 'MMM yyyy')}`;
}

/** Months to a readable list of runs: "Jan–Feb 2026 and Apr 2026". */
function monthsLabel(months: number[]): string {
  const runs: [number, number][] = [];
  for (const month of months) {
    const run = runs[runs.length - 1];
    if (run && run[1] === month - 1) run[1] = month;
    else runs.push([month, month]);
  }
  const labels = runs.map(([first, last]) => rangeLabel(first, last));
  return labels.length === 1
    ? labels[0]
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * The months the periods cover. A month counts once at least half of it is on
 * file, because banks print periods like 31 May – 30 Jun, which is June's
 * statement and not two months. Overlapping statements count once.
 */
function coveredMonths(periods: StatementPeriod[]): number[] {
  const days = new Set<number>();
  for (const period of periods) {
    const end = dayNumber(period.end);
    for (let day = dayNumber(period.start); day <= end; day++) days.add(day);
  }
  const daysByMonth = new Map<number, number>();
  for (const day of days) {
    const month = monthIndex(day);
    daysByMonth.set(month, (daysByMonth.get(month) ?? 0) + 1);
  }
  const covered = [...daysByMonth.entries()]
    .filter(([month, count]) => count * 2 >= daysInMonth(month))
    .map(([month]) => month);
  // A statement shorter than half a month still tells us the account was active then.
  return (covered.length > 0 ? covered : [Math.max(...daysByMonth.keys())]).sort((a, b) => a - b);
}

/**
 * What one account's statements cover and what to add next (FC-DET-047, §A3.7).
 * This decides no condition: the step asks the client to act on it, and lets them carry on.
 * Returns `null` when no statement has a usable period.
 */
export function statementCoverage(
  periods: StatementPeriod[],
  today: Date,
): StatementCoverage | null {
  const usable = periods.filter((p) => dayNumber(p.end) >= dayNumber(p.start));
  if (usable.length === 0) return null;

  const covered = coveredMonths(usable);
  const onFile = new Set(covered);
  const latestEnd = Math.max(...usable.map((p) => dayNumber(p.end)));
  const todayNumber = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) / MS_PER_DAY;
  const outOfDate = todayNumber - latestEnd > STALE_AFTER_DAYS;

  // Current statements: the months up to the newest one. Old ones: the months
  // up to the last complete month, because the old months no longer stand in.
  const newestWanted = outOfDate ? monthIndex(todayNumber) - 1 : covered[covered.length - 1];
  const toAdd: number[] = [];
  for (let month = newestWanted - MONTHS_ASKED + 1; month <= newestWanted; month++) {
    if (!onFile.has(month)) toAdd.push(month);
  }

  if (outOfDate) {
    const ended = formatDate(latestEnd * MS_PER_DAY, 'd MMM yyyy', 'en-ZA', 'UTC');
    return {
      message:
        `Your latest statement ends ${ended}, more than ${STALE_AFTER_DAYS} days ago. ` +
        `Please add ${monthsLabel(toAdd)}.`,
      needsMore: true,
    };
  }

  const count = covered.length;
  const summary = `${count} month${count === 1 ? '' : 's'} on file (${monthsLabel(covered)}).`;
  return toAdd.length > 0
    ? {
        message: `${summary} We ask for 6–9 months, so please add ${monthsLabel(toAdd)}.`,
        needsMore: true,
      }
    : { message: `${summary} That covers what we ask for.`, needsMore: false };
}
